import { Form, Radio, Select, Tag } from 'antd';
import { ToggleHeader, ToggleSwitch } from '@/components/toggle-switch';
import { cn } from '@/libs/utils';
import { formatHexColor } from '@/utils/formUtils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/** Salary calculation type, confirmations, work types and export options. */
export default function CompanyPayrollFields() {
  const {
    enumsDataIsLoading,
    extendedAbExportKeys,
    extendedAbExportState,
    form,
    hasSalaryCalculationType,
    holidayLeaveConfirmationKeys,
    holidayLeaveConfirmationState,
    inSection,
    isProfile,
    overTime2Keys,
    overTime2State,
    paidLeaveConfirmationKeys,
    paidLeaveConfirmationState,
    publicHolidayConfirmationKeys,
    publicHolidayConfirmationState,
    salaryCalculationOptions,
    salaryCalculationTypeKeys,
    salaryCalculationTypeState,
    sectionClass,
    sickLeaveUpTo30Days100ConfirmationKeys,
    sickLeaveUpTo30Days100ConfirmationState,
    sickLeaveUpTo30Days65ConfirmationKeys,
    sickLeaveUpTo30Days65ConfirmationState,
    t,
    workTypesKeys,
    workTypesOptions,
    workTypesState,
  } = useCompanyGeneralDataForm();
  // On the profile: laid out by CompanyPayrollProfilePanel.
  if (isProfile) return null;

  return (
    <>
      {salaryCalculationTypeState?.visible && (
        <Form.Item
          hidden={!inSection('obracun')}
          name={salaryCalculationTypeKeys}
          label={t('fixedAverageCalculation')}
          className="!mb-5"
        >
          <Radio.Group
            className="custom-ant-radio-label custom-ant-radio-group"
            options={salaryCalculationOptions}
            disabled={salaryCalculationTypeState?.disabled}
          />
        </Form.Item>
      )}

      {hasSalaryCalculationType && (
        <div
          className={cn(
            'mb-5 rounded-md border bg-zinc-100 p-4',
            sectionClass('obracun')
          )}
        >
          {holidayLeaveConfirmationState?.visible && (
            <Form.Item
              name={holidayLeaveConfirmationKeys}
              label={t('holidayLeaveConfirmation', { ns: 'forms' })}
            >
              <Radio.Group
                className="custom-ant-radio-label custom-ant-radio-group"
                options={salaryCalculationOptions}
                disabled={holidayLeaveConfirmationState?.disabled}
              />
            </Form.Item>
          )}

          {sickLeaveUpTo30Days65ConfirmationState?.visible && (
            <Form.Item
              name={sickLeaveUpTo30Days65ConfirmationKeys}
              label={t('sickLeaveUpTo30Days65Confirmation', { ns: 'forms' })}
            >
              <Radio.Group
                className="custom-ant-radio-label custom-ant-radio-group"
                options={salaryCalculationOptions}
                disabled={sickLeaveUpTo30Days65ConfirmationState?.disabled}
              />
            </Form.Item>
          )}

          {sickLeaveUpTo30Days100ConfirmationState?.visible && (
            <Form.Item
              name={sickLeaveUpTo30Days100ConfirmationKeys}
              label={t('sickLeaveUpTo30Days100Confirmation', { ns: 'forms' })}
            >
              <Radio.Group
                className="custom-ant-radio-label custom-ant-radio-group"
                options={salaryCalculationOptions}
                disabled={sickLeaveUpTo30Days100ConfirmationState?.disabled}
              />
            </Form.Item>
          )}

          {paidLeaveConfirmationState?.visible && (
            <Form.Item
              name={paidLeaveConfirmationKeys}
              label={t('paidLeaveConfirmation', { ns: 'forms' })}
            >
              <Radio.Group
                className="custom-ant-radio-label custom-ant-radio-group"
                options={salaryCalculationOptions}
              />
            </Form.Item>
          )}

          {publicHolidayConfirmationState?.visible && (
            <Form.Item
              name={publicHolidayConfirmationKeys}
              label={t('publicHolidayConfirmation', { ns: 'forms' })}
            >
              <Radio.Group
                className="custom-ant-radio-label custom-ant-radio-group"
                options={salaryCalculationOptions}
                disabled={publicHolidayConfirmationState?.disabled}
              />
            </Form.Item>
          )}
        </div>
      )}

      {workTypesState?.visible && (
        <Form.Item
          hidden={!inSection('obracun')}
          name={workTypesKeys}
          label={t('typesOfWork', { ns: 'forms' })}
        >
          <WorkTypesSelect
            options={workTypesOptions}
            loading={enumsDataIsLoading}
            disabled={workTypesState?.disabled}
          />
        </Form.Item>
      )}

      {overTime2State?.visible && (
        <div className={sectionClass('obracun')}>
          <ToggleHeader
            title={t('overtime2', { ns: 'forms' })}
            className="mb-5"
          >
            <ToggleSwitch
              name={overTime2Keys}
              form={form}
              disabled={overTime2State?.disabled}
            />
          </ToggleHeader>
        </div>
      )}

      {extendedAbExportState?.visible && (
        <div className={sectionClass('obracun')}>
          <ToggleHeader
            title={t('extendedAbExport', {
              ns: 'forms',
              defaultValue: 'Extended AB export (columns 50–60)',
            })}
            className="mb-5"
          >
            <ToggleSwitch
              name={extendedAbExportKeys}
              form={form}
              disabled={extendedAbExportState?.disabled}
            />
          </ToggleHeader>
        </div>
      )}
    </>
  );
}

/**
 * Work types multi-select with the work types' colours; extra props (value,
 * onChange from Form.Item) go through to the Select.
 */
export function WorkTypesSelect({ options, loading, disabled, ...props }) {
  return (
    <Select
      {...props}
      mode="multiple"
      className="w-full"
      placement="topLeft"
      options={options}
      loading={loading}
      disabled={disabled}
      tagRender={({ label, value, closable, onClose }) => {
        const option = options.find((option) => option.value === value);

        const tagColor = formatHexColor(option?.color?.argb);
        const textColor = formatHexColor(option?.textColor?.argb);

        const onPreventMouseDown = (event) => {
          event.preventDefault();
          event.stopPropagation();
        };

        return (
          <Tag
            color={tagColor}
            style={{ color: textColor, marginInlineEnd: 4 }}
            closable={closable}
            onClose={onClose}
            onMouseDown={onPreventMouseDown}
          >
            {label}
          </Tag>
        );
      }}
      optionRender={({ label, data: { color, textColor, disabled } }) => {
        const tagColor = formatHexColor(color?.argb);
        const textColorHex = formatHexColor(textColor?.argb);
        const opacity = disabled ? 0.5 : 1;

        return (
          <Tag
            color={tagColor}
            style={{
              color: textColorHex,
              opacity,
              marginInlineEnd: 4,
            }}
          >
            <strong className="font-medium">{label}</strong>
          </Tag>
        );
      }}
    />
  );
}
